import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { GenerateReportRequest } from 'features/lead-management/types/request';

import { TimeZones } from '@constants/date';
import {
  SubscriptionPlanExpiredMessage,
  SubscriptionStatus
} from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { SUBSCRIPTIONS } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import {
  addDownload,
  updateDownloadStatus
} from '@features/downloads/downloadsSlice';
import { formatDateRange } from '@features/group-management/helper';
import useNotify from '@hooks/notify';
import { ApiError, DateRange } from '@types';
import { getFormattedDateTimeWithOffset } from '@utils/date';
import usePollDownloadStatus from '@utils/pollDownloadStatus';
import { buildQueryString } from '@utils/queryStringBuilder';
import { getLocalStorageItem } from '@utils/storage';

import {
  useGenerateLeadReportMutation,
  useLazyGetCustomReportJobIdQuery,
  useLazyGetGstReportJobIdQuery
} from '../queries/ReportsQuery';

const useDownloadReport = () => {
  const { createErrorAlert } = useNotify();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pollDownloadStatus, pollLeadReportJobStatus } =
    usePollDownloadStatus();
  const [getGstReportJobId] = useLazyGetGstReportJobIdQuery();
  const [getCustomReportJobId] = useLazyGetCustomReportJobIdQuery();
  const [getReportJobStatus] = useGenerateLeadReportMutation();

  const { rolesAndPermissions } = useSelector((state: any) => state.user);
  const { currentSubscription } = useSelector((state: any) => state.user);
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  const formattedDate = (date: string | Date, isEndDate = false) =>
    getFormattedDateTimeWithOffset(date, TimeZones['Asia/Kolkata'], isEndDate);

  const handleDownloadGstReport = async (
    selectedDateRange: DateRange,
    selectedBranchIds: string[],
    defaultBranchId: string,
    isMarkAsPaidChecked: boolean
  ) => {
    if (currentSubscription?.status === SubscriptionStatus.EXPIRED) {
      createErrorAlert(SubscriptionPlanExpiredMessage);
      if (
        rolesAndPermissions.permissions.includes(
          UserPermissionTypes.SUBSCRIPTION_VIEW
        )
      ) {
        navigate(SUBSCRIPTIONS.PLAN_DETAILS);
      }
    } else {
      const { startDate, endDate } = formatDateRange(selectedDateRange);
      const queryObject = {
        from_timestamp: formattedDate(startDate),
        to_timestamp: formattedDate(endDate, true),
        branch_id: selectedBranchIds.join(','),
        payment_mode: isMarkAsPaidChecked ? 'all' : 'automatic'
      };
      const queryString = buildQueryString(queryObject);

      const downloadId = `gst-report-${Date.now()}`;

      const res = await getGstReportJobId({
        params: {
          organizationId,
          branchId: defaultBranchId
        },
        queryString
      });

      if (res?.data) {
        const jobId = res.data?.job_id;
        if (jobId) {
          dispatch(
            addDownload({
              id: downloadId,
              fileName: downloadId,
              fileType: 'xlsx'
            })
          );
          const fileType = 'gst_report';

          await pollDownloadStatus({
            jobId,
            downloadId,
            mixpanelData: {
              fileType,
              selectedBranchIds,
              includeMarkAsPaidChecked: isMarkAsPaidChecked
            }
          });
        }
        sendMixpanelEvent(Event.DOWNLOAD_GST_REPORT_CLICKED, {
          startDate,
          endDate,
          selectedBranchIds,
          includeMarkAsPaidChecked: isMarkAsPaidChecked
        });
      } else if ('error' in res) {
        const error = res.error as ApiError;
        const message =
          error?.status === 426
            ? 'subscription plan has expired'
            : 'Error while downloading report';
        dispatch(
          updateDownloadStatus({
            id: downloadId,
            status: 'error',
            error: message
          })
        );
        createErrorAlert(message);
        if (error?.status === 426) {
          if (
            rolesAndPermissions.permissions.includes(
              UserPermissionTypes.SUBSCRIPTION_VIEW
            )
          ) {
            navigate(SUBSCRIPTIONS.PLAN_DETAILS);
          }
        }
        sendMixpanelEvent(Event.DOWNLOAD_GST_REPORT_CLICKED, {
          error: message
        });
      }
    }
  };

  const handleDownloadCustomReport = async (
    selectedDateRange: DateRange,
    selectedBranchIds: string[],
    defaultBranchId: string,
    isQuickCollectChecked: boolean,
    selectCategory: string
  ) => {
    if (currentSubscription?.status === SubscriptionStatus.EXPIRED) {
      createErrorAlert(SubscriptionPlanExpiredMessage);
      if (
        rolesAndPermissions.permissions.includes(
          UserPermissionTypes.SUBSCRIPTION_VIEW
        )
      ) {
        navigate(SUBSCRIPTIONS.PLAN_DETAILS);
      }
    } else {
      const { startDate, endDate } = formatDateRange(selectedDateRange);
      const queryObject = {
        from_timestamp: formattedDate(startDate),
        to_timestamp: formattedDate(endDate, true),
        branch_id: selectedBranchIds.join(','),
        include_mark_as_paid: true,
        include_quick_collect: isQuickCollectChecked,
        category: selectCategory
      };
      const queryString = buildQueryString(queryObject);

      const downloadId = `custom-report-${Date.now()}`;

      const res = await getCustomReportJobId({
        params: {
          organizationId,
          branchId: defaultBranchId
        },
        queryString
      });
      if (res?.data) {
        const jobId = res.data?.job_id;
        if (jobId) {
          dispatch(
            addDownload({
              id: downloadId,
              fileName: downloadId,
              fileType: 'xlsx'
            })
          );

          const fileType = 'custom_report';

          await pollDownloadStatus({
            jobId,
            downloadId,
            mixpanelData: {
              fileType,
              startDate,
              endDate,
              selectedBranchIds,
              includeMarkAsPaidChecked: true
            }
          });
        }
        sendMixpanelEvent(Event.DOWNLOAD_CUSTOM_REPORT_CLICKED, {
          startDate,
          endDate,
          selectedBranchIds,
          includeMarkAsPaidChecked: true,
          isQuickCollectChecked,
          categorySelected: selectCategory
        });
      } else if ('error' in res) {
        const error = res.error as ApiError;
        const message =
          error?.status === 426
            ? 'subscription plan has expired'
            : 'Error while downloading report';
        createErrorAlert(message);
        dispatch(
          updateDownloadStatus({
            id: downloadId,
            status: 'error',
            error: message
          })
        );
        if (error?.status === 426) {
          if (
            rolesAndPermissions.permissions.includes(
              UserPermissionTypes.SUBSCRIPTION_VIEW
            )
          ) {
            navigate(SUBSCRIPTIONS.PLAN_DETAILS);
          }
        }
        sendMixpanelEvent(Event.DOWNLOAD_CUSTOM_REPORT_ERROR, {
          error: message
        });
      }
    }
  };

  const handleDownloadLeadReport = async (
    orgId: string,
    payload: GenerateReportRequest
  ) => {
    if (currentSubscription?.status === SubscriptionStatus.EXPIRED) {
      createErrorAlert(SubscriptionPlanExpiredMessage);
      if (
        rolesAndPermissions.permissions.includes(
          UserPermissionTypes.SUBSCRIPTION_VIEW
        )
      ) {
        navigate(SUBSCRIPTIONS.PLAN_DETAILS);
      }
    } else {
      const downloadId = `${payload.report_type}-${Date.now()}`;
      const result = await getReportJobStatus({
        organizationId: orgId,
        payload
      });
      if ('data' in result && result.data) {
        const jobId = result.data?.job_id;
        if (jobId) {
          dispatch(
            addDownload({
              id: downloadId,
              fileName: downloadId,
              fileType: 'xlsx'
            })
          );

          await pollLeadReportJobStatus({
            jobId,
            downloadId
          });
        }
      }
    }
  };

  return {
    handleDownloadGstReport,
    handleDownloadCustomReport,
    handleDownloadLeadReport
  };
};

export default useDownloadReport;
